import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { buildRoutes, getActiveModule, resolveLandingRedirect } from './routes'
import AppEnvService from "@core/services/utils/appEnvService";
import { useAppMenuStore } from '@core/stores/app/appMenuStore'

const createHistory = process.env.SERVER
  ? createMemoryHistory
  : (process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

// Mode hash (développement) : le routeur ne lit que la partie après « # ». Un lien par chemin (e-mail
// d'invitation, shortlist publique, certificat : …/candidate-auth?inviteToken=…) atterrissait donc sur « #/ ».
// On le réécrit en …/#/candidate-auth?… pour qu'il fonctionne aussi en développement.
if (createHistory === createWebHashHistory && typeof window !== 'undefined') {
  const { pathname, search, hash, origin } = window.location
  if (pathname !== '/' && !hash) window.location.replace(`${origin}/#${pathname}${search}`)
}

const Router = createRouter({
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    } else {
      return {
        top: 0,
        behavior: 'smooth'
      }
    }
  },
  routes: buildRoutes(),
  history: createHistory(process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE)
})

/**
 * Called after AppEnvService.load() completes.
 *
 * The landing redirect is already dynamic (function-based) so it picks up
 * APP_LANDING_PAGE without any route mutation. This function logs the resolved
 * config and re-triggers navigation when the user is on '/', on the portal
 * redirect, or stuck on the '/no-landing' fallback (which can happen when env
 * wasn't loaded at the time of the initial route resolution).
 */
export const rebuildRoutes = () => {
  const active = getActiveModule()
  const landing = resolveLandingRedirect()
  console.log(`[Router] Env loaded | active module: ${active.code} | landing: ${landing}`)

  const current = Router.currentRoute.value
  const stuckOnFallback = current.path === '/no-landing' && landing !== '/no-landing'
  if (current.path === '/' || current.name === 'portal' || stuckOnFallback) {
    Router.replace(landing)
  }
}

Router.beforeEach((to, from, next) => {
  // If the user navigates to an unknown URL after the menu has already been loaded
  // (i.e. dynamic routes are in place), the catch-all → Loading.vue would otherwise
  // hang. Short-circuit to ErrorNotFound. On initial refresh (menu not yet loaded),
  // we let Loading.vue render and routerService.applyRoutes() will replay later.
  if (to.name === 'not-found') {
    try {
      const menuStore = useAppMenuStore()
      if (menuStore.dynamicRoutes?.length) {
        next({ name: 'error-not-found', replace: true })
        return
      }
    } catch (e) {
      // Pinia not ready yet — fall through and let Loading.vue render
    }
  }
  next()
})

export default Router
